/**
 * 状態異常システム: タイプ定義
 *
 * このファイルは状態異常の全タイプとメタデータを定義します。
 *
 * @see src/ルール/status-effect-system-design.md
 */

import type { CardAttribute } from '../../types/card';

/**
 * 状態異常タイプの定義
 */
export const STATUS_EFFECT_TYPES = {
  // ========================================
  // 行動制限系（モンスター）
  // ========================================

  /**
   * 眠り
   * - 攻撃不可
   * - 効果発動不可（基本技、上級技、トリガー）
   * - ターン開始時50%で解除、または次ターン終了時に解除
   */
  SLEEP: 'SLEEP',

  /**
   * 凍結
   * - 攻撃力50%ダウン
   * - 攻撃不可
   * - 次ターン開始時50%で解除
   */
  FREEZE: 'FREEZE',

  /**
   * 雷撃
   * - 攻撃力-500
   * - 技不能（基本技、上級技使用不可）
   * - 次のターン終了時に解除
   */
  THUNDER: 'THUNDER',

  /**
   * 行動不能（スタン）
   * - 攻撃不可
   * - 効果発動不可
   * - 指定ターン後に解除
   */
  STUN: 'STUN',

  /**
   * 檻（ケージ）
   * - 攻撃不可
   * - 効果発動不可（技・トリガー）
   * - 相手ターン開始時にSP1で解除可能
   * - 解除されるまで永続
   */
  CAGED: 'CAGED',

  // ========================================
  // 効果制限系（モンスター）
  // ========================================

  /**
   * 効果無効（サイレンス）
   * - 効果発動不可
   * - 攻撃は可能
   * - 指定ターン後に解除
   */
  SILENCE: 'SILENCE',

  // ========================================
  // 被ダメージ増加系（モンスター）
  // ========================================

  /**
   * 濡れ
   * - 受けるダメージ2倍
   * - 次のターン終了時に解除
   */
  WET: 'WET',

  // ========================================
  // 防御系（モンスター）
  // ========================================

  /**
   * 守護
   * - 1度だけダメージを半減
   * - ダメージを受けた時に消費
   */
  GUARD: 'GUARD',

  /**
   * 無敵（免疫）
   * - ダメージを受けない
   * - 指定ターン後に解除
   */
  INVINCIBLE: 'INVINCIBLE',

  // ========================================
  // 継続ダメージ/デバフ系（モンスター）
  // ========================================

  /**
   * 深蝕
   * - エンドフェイズに攻撃力減少
   * - 解除されるまで継続（永続）
   */
  CORRODE: 'CORRODE',

  // ========================================
  // バフ系（モンスター - 正の状態異常）
  // ========================================

  /**
   * 覚醒
   * - 攻撃力が上昇（カードにより効果値異なる）
   * - ターン終了時に解除
   */
  AWAKENED: 'AWAKENED',

  /**
   * 攻撃力上昇（一時的）
   * - 攻撃力が上昇
   * - 指定ターン後に解除
   */
  ATK_UP: 'ATK_UP',

  /**
   * HP上昇（一時的）
   * - HPが上昇
   * - 指定ターン後に解除
   */
  HP_UP: 'HP_UP',

  /**
   * 攻撃力低下（一時的）
   * - 攻撃力が低下
   * - エンドフェイズ回数で解除（expiresAfterEndPhases）
   */
  ATK_DOWN: 'ATK_DOWN',

  /**
   * 寄生（粘液獣・寄生）
   * - 毎ターン開始時に攻撃力減少（500 or 1000）
   * - 効果無効化（技・トリガー使用不可）
   * - 相手のエンドフェイズで効果無効のみ解除
   * - 寄生カード参照を保持（破壊時墓地送り用）
   */
  PARASITE: 'PARASITE',

  // ========================================
  // プレイヤー状態異常
  // ========================================

  /**
   * 毒（プレイヤー）
   * - 毎ターン終了時に固定ダメージ（100）
   * - 解除されるまで継続（永続）
   */
  POISON: 'POISON',

  // ========================================
  // プレイヤーバフ（正の状態異常）
  // ========================================

  /**
   * 光属性召喚コスト軽減（プレイヤー）
   * - 光属性モンスターの召喚コストを軽減
   * - 次の自分のターン終了時に解除
   * - クリスタルブレス等で付与
   */
  LIGHT_SUMMON_COST_REDUCTION: 'LIGHT_SUMMON_COST_REDUCTION',

  /**
   * SP増加減少（プレイヤー）
   * - 次のターンのSP増加量を減少させる
   * - 次の自分のターン開始時（SP増加処理後）に解除
   * - ダーク・リヴァイアサン等で付与
   */
  SP_GAIN_REDUCTION: 'SP_GAIN_REDUCTION',

  /**
   * ダイレクトアタック禁止（プレイヤー）
   * - このターン中ダイレクトアタックできない
   * - ターン終了時に解除
   * - ダーク・リヴァイアサン、ブラック・オラクル等で付与
   */
  NO_DIRECT_ATTACK: 'NO_DIRECT_ATTACK',

  // ========================================
  // 特殊状態異常（モンスター）
  // ========================================

  /**
   * ダメージシールド
   * - 受けるダメージを固定値軽減（valueで指定）
   * - エンドフェイズ回数で解除（expiresAfterEndPhases）
   * - 軽減は毎回のダメージに適用（回数制限なし）
   */
  DAMAGE_SHIELD: 'DAMAGE_SHIELD',

  /**
   * 彷徨い（さまよい）
   * - 攻撃力が0になる
   * - 【飛行】状態になる（対空でないと迎撃されない）
   * - 技を発動できない（トリガーは有効）
   * - 【飛行】を持つモンスターは【彷徨い】にならない（耐性）
   * - 3ターン経過で自動解除
   */
  WANDERING: 'WANDERING',
} as const;

/** 状態異常タイプの値の型 */
export type StatusEffectTypeValue = (typeof STATUS_EFFECT_TYPES)[keyof typeof STATUS_EFFECT_TYPES];

/**
 * 状態異常の対象を判定
 */
export const STATUS_EFFECT_TARGETS = {
  MONSTER: 'monster',
  PLAYER: 'player',
} as const;

/** 状態異常対象の値の型 */
export type StatusEffectTarget = (typeof STATUS_EFFECT_TARGETS)[keyof typeof STATUS_EFFECT_TARGETS];

/**
 * 状態異常がどの対象に付与されるか
 */
export const getStatusEffectTarget = (statusType: StatusEffectTypeValue): StatusEffectTarget => {
  switch (statusType) {
    case STATUS_EFFECT_TYPES.POISON:
    case STATUS_EFFECT_TYPES.LIGHT_SUMMON_COST_REDUCTION:
    case STATUS_EFFECT_TYPES.SP_GAIN_REDUCTION:
    case STATUS_EFFECT_TYPES.NO_DIRECT_ATTACK:
      return STATUS_EFFECT_TARGETS.PLAYER;
    default:
      return STATUS_EFFECT_TARGETS.MONSTER;
  }
};

/** 状態異常カテゴリ */
export type StatusEffectCategory = 'buff' | 'debuff';

/** 状態異常の効果定義 */
interface StatusEffectDefinition {
  canAttack?: boolean;
  canUseSkill?: boolean;
  canUseTrigger?: boolean;
  attackMultiplier?: number;
  attackFlatModifier?: number;
  attackSetToZero?: boolean;  // 攻撃力を0にする（彷徨い用）
  grantsFlying?: boolean;     // 飛行状態を付与（彷徨い用）
  hpFlatModifier?: number;
  damageMultiplier?: number;
  damageReduction?: number;
  damageImmunity?: boolean;
  endPhaseAtkDown?: boolean;
  endPhaseDamage?: number;
  turnStartAtkDown?: boolean;
  summonCostReduction?: number;
  targetAttribute?: CardAttribute;
  spGainReduction?: number;
}

/** 状態異常メタデータエントリ */
interface StatusEffectMetadataEntry {
  displayName: string;
  icon: string;
  color: string;
  category: StatusEffectCategory;
  target: StatusEffectTarget;
  effects: StatusEffectDefinition;
  defaultDuration?: number;
  defaultRemoveChance?: number;
  maxUsage?: number;
  removeOnUse?: boolean;
  spReleaseCost?: number;
}

/**
 * 状態異常メタデータ
 */
export const STATUS_EFFECT_METADATA: Record<StatusEffectTypeValue, StatusEffectMetadataEntry> = {
  // ========================================
  // モンスター状態異常
  // ========================================

  [STATUS_EFFECT_TYPES.SLEEP]: {
    displayName: '眠り',
    icon: '💤',
    color: '#9966cc',
    category: 'debuff',
    target: 'monster',
    effects: {
      canAttack: false,
      canUseSkill: false,
      canUseTrigger: false,
    },
    defaultDuration: 1,
    defaultRemoveChance: 0.5,
  },
  [STATUS_EFFECT_TYPES.FREEZE]: {
    displayName: '凍結',
    icon: '❄️',
    color: '#00bfff',
    category: 'debuff',
    target: 'monster',
    effects: {
      canAttack: false,
      attackMultiplier: 0.5, // 攻撃力50%
    },
    defaultDuration: 1,
    defaultRemoveChance: 0.5,
  },
  [STATUS_EFFECT_TYPES.THUNDER]: {
    displayName: '雷撃',
    icon: '⚡',
    color: '#ffd700',
    category: 'debuff',
    target: 'monster',
    effects: {
      attackFlatModifier: -500, // 攻撃力-500（固定値）
      canUseSkill: false,
    },
    defaultDuration: 1,
  },
  [STATUS_EFFECT_TYPES.WET]: {
    displayName: '濡れ',
    icon: '💧',
    color: '#1e90ff',
    category: 'debuff',
    target: 'monster',
    effects: {
      damageMultiplier: 2.0, // 受けるダメージ2倍
    },
    defaultDuration: 1,
  },
  [STATUS_EFFECT_TYPES.STUN]: {
    displayName: '行動不能',
    icon: '🚫',
    color: '#ff6347',
    category: 'debuff',
    target: 'monster',
    effects: {
      canAttack: false,
      canUseSkill: false,
    },
    defaultDuration: 1,
  },
  [STATUS_EFFECT_TYPES.CAGED]: {
    displayName: '檻',
    icon: '🔒',
    color: '#8b4513',
    category: 'debuff',
    target: 'monster',
    effects: {
      canAttack: false,
      canUseSkill: false,
      canUseTrigger: false,
    },
    defaultDuration: -1, // 永続（SP解除まで）
    spReleaseCost: 1,    // SP1で解除可能
  },
  [STATUS_EFFECT_TYPES.SILENCE]: {
    displayName: '効果無効',
    icon: '🔇',
    color: '#888888',
    category: 'debuff',
    target: 'monster',
    effects: {
      canUseSkill: false,
      canUseTrigger: false,
    },
    defaultDuration: 1,
  },
  [STATUS_EFFECT_TYPES.GUARD]: {
    displayName: '守護',
    icon: '🛡️',
    color: '#4a90d9',
    category: 'buff',
    target: 'monster',
    effects: {
      damageReduction: 0.5, // 50%軽減
    },
    maxUsage: 1,
    removeOnUse: true,
  },
  [STATUS_EFFECT_TYPES.INVINCIBLE]: {
    displayName: '無敵',
    icon: '✨',
    color: '#ffd700',
    category: 'buff',
    target: 'monster',
    effects: {
      damageImmunity: true,
    },
    defaultDuration: 1,
  },
  [STATUS_EFFECT_TYPES.CORRODE]: {
    displayName: '深蝕',
    icon: '🦠',
    color: '#556b2f',
    category: 'debuff',
    target: 'monster',
    effects: {
      endPhaseAtkDown: true,
    },
    defaultDuration: -1, // 永続
  },
  [STATUS_EFFECT_TYPES.AWAKENED]: {
    displayName: '覚醒',
    icon: '🔥',
    color: '#ff4500',
    category: 'buff',
    target: 'monster',
    effects: {
      attackFlatModifier: 0, // カードにより異なる、valueで指定
    },
    defaultDuration: 0, // ターン終了時まで
  },
  [STATUS_EFFECT_TYPES.ATK_UP]: {
    displayName: '攻撃力上昇',
    icon: '⬆️',
    color: '#32cd32',
    category: 'buff',
    target: 'monster',
    effects: {
      attackFlatModifier: 0, // valueで指定
    },
    defaultDuration: 1,
  },
  [STATUS_EFFECT_TYPES.HP_UP]: {
    displayName: 'HP上昇',
    icon: '💚',
    color: '#32cd32',
    category: 'buff',
    target: 'monster',
    effects: {
      hpFlatModifier: 0, // valueで指定
    },
    defaultDuration: 1,
  },
  [STATUS_EFFECT_TYPES.ATK_DOWN]: {
    displayName: '攻撃力低下',
    icon: '⬇️',
    color: '#ff6666',
    category: 'debuff',
    target: 'monster',
    effects: {
      attackFlatModifier: 0, // valueで指定（負の値）
    },
    // expiresAfterEndPhasesで管理（durationは使用しない）
    defaultDuration: -1,
  },
  [STATUS_EFFECT_TYPES.PARASITE]: {
    displayName: '寄生',
    icon: '🦠',
    color: '#8B4513',
    category: 'debuff',
    target: 'monster',
    effects: {
      canUseSkill: false,     // 効果無効化
      canUseTrigger: false,   // トリガー無効化
      turnStartAtkDown: true, // ターン開始時ATK減少
    },
    defaultDuration: -1, // 永続（特殊処理で管理）
  },

  // ========================================
  // プレイヤー状態異常
  // ========================================

  [STATUS_EFFECT_TYPES.POISON]: {
    displayName: '毒',
    icon: '☠️',
    color: '#9932cc',
    category: 'debuff',
    target: 'player',
    effects: {
      endPhaseDamage: 100, // 毎ターン100ダメージ
    },
    defaultDuration: -1, // 永続
  },

  // ========================================
  // プレイヤーバフ
  // ========================================

  [STATUS_EFFECT_TYPES.LIGHT_SUMMON_COST_REDUCTION]: {
    displayName: '光輝の祝福',
    icon: '💎',
    color: '#ffd700',
    category: 'buff',
    target: 'player',
    effects: {
      summonCostReduction: 1, // 召喚コスト軽減量
      targetAttribute: '光',  // 対象属性
    },
    defaultDuration: 1, // 次の自分のターン終了時まで
  },

  [STATUS_EFFECT_TYPES.SP_GAIN_REDUCTION]: {
    displayName: '深淵の呪縛',
    icon: '🌀',
    color: '#1e3a5f',
    category: 'debuff',
    target: 'player',
    effects: {
      spGainReduction: 1, // SP増加減少量
    },
    defaultDuration: 0, // SP増加処理後に即解除（特殊処理）
  },

  [STATUS_EFFECT_TYPES.NO_DIRECT_ATTACK]: {
    displayName: 'ダイレクト禁止',
    icon: '🚷',
    color: '#8b0000',
    category: 'debuff',
    target: 'player',
    effects: {},
    defaultDuration: 0, // ターン終了時に解除（特殊処理）
  },

  // ========================================
  // 特殊状態異常（モンスター）
  // ========================================

  [STATUS_EFFECT_TYPES.DAMAGE_SHIELD]: {
    displayName: 'ダメージ軽減',
    icon: '🛡️',
    color: '#7b68ee',
    category: 'buff',
    target: 'monster',
    effects: {
      // valueで軽減量を指定（固定値）
    },
    // expiresAfterEndPhasesで管理
    defaultDuration: -1,
  },

  [STATUS_EFFECT_TYPES.WANDERING]: {
    displayName: '彷徨い',
    icon: '👻',
    color: '#b0c4de',
    category: 'debuff',
    target: 'monster',
    effects: {
      attackSetToZero: true,  // 攻撃力が0になる
      grantsFlying: true,     // 飛行状態になる
      canUseSkill: false,     // 技を発動できない
      // canUseTrigger は true（トリガーは有効）
    },
    defaultDuration: 3, // 3ターン経過で自動解除
  },
};

/**
 * 状態異常の表示名を取得
 * @param statusType - 状態異常タイプ
 * @returns 表示名
 */
export const getStatusDisplayName = (statusType: StatusEffectTypeValue): string => {
  return STATUS_EFFECT_METADATA[statusType]?.displayName || statusType;
};

/**
 * 状態異常のアイコンを取得
 * @param statusType - 状態異常タイプ
 * @returns アイコン
 */
export const getStatusIcon = (statusType: StatusEffectTypeValue): string => {
  return STATUS_EFFECT_METADATA[statusType]?.icon || '❓';
};

/**
 * 状態異常がデバフかどうか
 * @param statusType - 状態異常タイプ
 * @returns boolean
 */
export const isDebuff = (statusType: StatusEffectTypeValue): boolean => {
  return STATUS_EFFECT_METADATA[statusType]?.category === 'debuff';
};

/**
 * 状態異常がバフかどうか
 * @param statusType - 状態異常タイプ
 * @returns boolean
 */
export const isBuff = (statusType: StatusEffectTypeValue): boolean => {
  return STATUS_EFFECT_METADATA[statusType]?.category === 'buff';
};
